import { z } from 'zod';
import { booleanParam, PaginationMetaSchema } from './pagination.js';

/**
 * Technical-reception surveys ("recepție tehnică") — the polygon a
 * commission accepted on the ground for one campaign.
 *
 * Separate from `Parcel` on purpose: these overlap the UCF parcels they
 * were surveyed against, so they must never be counted as parcels. They
 * carry a `parcelId` link resolved by point-in-polygon instead.
 */

export const SezonRecepcieSchema = z.enum(['PRIMAVARA', 'TOAMNA']);
export type SezonRecepcie = z.infer<typeof SezonRecepcieSchema>;

/** Parcel slice shown alongside a survey; mirrors PhotoParcelBrief. */
export const ReceptieParcelBriefSchema = z.object({
  id: z.string().uuid(),
  ucf: z.string().nullable(),
  codProiect: z.string().nullable(),
  raion: z.string().nullable(),
  primarie: z.string().nullable(),
  entitateSilvica: z.string().nullable(),
  suprafata: z.number().nullable(),
  anul: z.number().int().nullable(),
});
export type ReceptieParcelBrief = z.infer<typeof ReceptieParcelBriefSchema>;

export const RecepcieTehnicaSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  importBatchId: z.string().uuid().nullable(),
  parcelId: z.string().uuid().nullable(),
  parcel: ReceptieParcelBriefSchema.nullable().optional(),

  sourceFile: z.string(),
  primarie: z.string(),
  codPrimarie: z.string().nullable(),
  raion: z.string().nullable(),
  entitateSilvica: z.string().nullable(),
  ucf: z.string(),
  parcelaNumber: z.string().nullable(),
  parcele: z.string(),

  /** Hectares as written in the archive name. */
  suprafataDeclarata: z.number().nullable(),
  /** Hectares computed from the polygon. Both are kept — they disagree
   *  by more than 15% on roughly one survey in eight. */
  suprafataCalculata: z.number().nullable(),

  anul: z.number().int(),
  sezon: SezonRecepcieSchema,

  geometryType: z.string().nullable(),
  geometry: z.unknown().nullable(),

  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type RecepcieTehnicaDto = z.infer<typeof RecepcieTehnicaSchema>;

export const ReceptieListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  sort: z
    .enum([
      'createdAt',
      'anul',
      'sezon',
      'primarie',
      'raion',
      'entitateSilvica',
      'ucf',
      'suprafataDeclarata',
      'suprafataCalculata',
    ])
    .default('createdAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
  search: z.string().optional(),
  /** CSV multi-selects, mirroring the parcels + photos list contracts. */
  raioane: z.string().optional(),
  primarii: z.string().optional(),
  entitatiSilvice: z.string().optional(),
  years: z.string().optional(),
  sezoane: z.string().optional(),
  hasParcel: booleanParam.optional(),
  includeDeleted: booleanParam.optional().default(false),
});
export type ReceptieListQuery = z.infer<typeof ReceptieListQuerySchema>;

export const ReceptieListResponseSchema = z.object({
  items: z.array(RecepcieTehnicaSchema),
  pagination: PaginationMetaSchema,
  totals: z
    .object({
      count: z.number(),
      suprafataDeclarata: z.number(),
      suprafataCalculata: z.number(),
    })
    .optional(),
});
export type ReceptieListResponse = z.infer<typeof ReceptieListResponseSchema>;

/** One archive as it appears in the import preview. */
export const ReceptieParsedRowSchema = z.object({
  sourceFile: z.string(),
  primarie: z.string(),
  ucf: z.string(),
  parcele: z.string(),
  suprafataDeclarata: z.number().nullable(),
  suprafataCalculata: z.number().nullable(),
  anul: z.number().int(),
  sezon: SezonRecepcieSchema,
  /** Set once the register resolved the mayoralty code. */
  codPrimarie: z.string().nullable(),
  raion: z.string().nullable(),
  entitateSilvica: z.string().nullable(),
  /** True when this survey already exists and the commit will skip it. */
  duplicate: z.boolean(),
});
export type ReceptieParsedRow = z.infer<typeof ReceptieParsedRowSchema>;

export const ReceptieParseResponseSchema = z.object({
  batchId: z.string().uuid(),
  importTarget: z.literal('receptie'),
  validCount: z.number(),
  invalidCount: z.number(),
  duplicateCount: z.number(),
  /** Archives whose .prj was MOLDREF99 and were converted to WGS84. */
  reprojectedCount: z.number(),
  campaigns: z.array(z.object({ anul: z.number().int(), sezon: SezonRecepcieSchema })),
  normalized: z.object({
    primarieMatched: z.number(),
    primarieUnmatched: z.number(),
  }),
  preview: z.array(ReceptieParsedRowSchema),
  errors: z.array(z.object({ sourceFile: z.string(), reason: z.string(), code: z.string().optional() })),
});
export type ReceptieParseResponse = z.infer<typeof ReceptieParseResponseSchema>;
